import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { useServiceError } from '@/hooks';

import { PageRequest } from '@/core/request/PageRequest';

import {
  createBankingEntry,
  reversalBankingEntry,
  getBankingEntry,
  listBankingEntries,
  updateBankingEntry,
} from './BankingEntry.service';

import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import {
  BankingEntryCreateRequest,
  BankingEntryQuery,
  BankingEntryReversalRequest,
  BankingEntryUpdateRequest,
} from './BankingEntry.types';

import { useIntl } from 'react-intl';
import { notification } from 'antd';
import notificationError from '@/utils/functions/notificationError';

export function useBankingEntries(params?: BankingEntryQuery) {
  const query = useInfiniteQuery(
    ['bankingEntries', { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listBankingEntries(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useCreateBankingEntry() {
  const intl = useIntl();

  const mutation = useMutation(
    (obj: BankingEntryCreateRequest) => createBankingEntry(obj),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'releaseBankEntry.create.notification.success',
          }),
          description: intl.formatMessage({
            id: 'releaseBankEntry.create.notification.successDescription',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useReversalBankingEntry() {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: BankingEntryReversalRequest) => reversalBankingEntry(obj),
    {
      onSuccess(_, data) {
        notification.success({
          message: 'Sucesso',
          description: 'Lançamento bancário estornado com sucesso!',
        });

        queryClient.invalidateQueries(['bankingEntries']);
        queryClient.invalidateQueries(['banking-entry-detail', data.id]);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useUpdateBankingEntry() {
  const intl = useIntl();

  return useMutation(
    (obj: BankingEntryUpdateRequest) => updateBankingEntry(obj),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'releaseBankEntry.update.notification.success',
          }),
          description: intl.formatMessage({
            id: 'releaseBankEntry.update.notification.successDescription',
          }),
        });
      },
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useBankingEntry(id: number) {
  const query = useQuery(['banking-entry-detail', id], () =>
    getBankingEntry(id),
  );

  useServiceError(query.error);

  return query;
}
